import { h } from "@stencil/core";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
let autoId = 0;
const cameraPreferenceDictionary = {
    FrontCamera: { facingMode: { exact: "user" } },
    BackCamera: { facingMode: { exact: "environment" } }
};
// TODO: Add a intersection observer to disable the scanner if it is out of
// screen to save resources
/**
 * This component allows you to scan a wide variety of types of barcode and QR
 * codes.
 */
export class ChBarcodeScanner {
    constructor() {
        this.barcodeBoxWidth = 200;
        this.barcodeBoxHeight = 200;
        this.cameraId = undefined;
        this.cameraPreference = "Default";
        this.readDebounce = 200;
        this.scanning = true;
    }
    #html5QrCode = null;
    #scannerId = null;
    #firstTime = true;
    #timeout;
    #resizeObserver;
    #cameraIdOrScanningChange = false;
    #cameras;
    #currentCameraId;
    // Track last scan
    #lastScan;
    #lastScanDateTime;
    el;
    cameraChange() {
        this.#cameraIdOrScanningChange = true;
    }
    scanningChange(newScanningValue) {
        if (newScanningValue) {
            this.#initializeScanning(newScanningValue);
        }
        else {
            this.#destroyScanner();
        }
    }
    /**
     * Fired when the control is first rendered. Contains the ids about all
     * available cameras.
     */
    cameras;
    /**
     * Fired when a new barcode is decoded.
     */
    read;
    /**
     * Scan a file a return a promise with the decoded text.
     */
    async scan(imageFile) {
        this.#html5QrCode ??= new Html5Qrcode(this.#scannerId);
        return this.#html5QrCode.scanFile(imageFile);
    }
    #onScanSuccess = (decodedText) => {
        const currentDateTime = new Date();
        if (this.#lastScan === decodedText) {
            const elapsedTimeBetweenSameRead = currentDateTime.getTime() - this.#lastScanDateTime.getTime();
            if (elapsedTimeBetweenSameRead < this.readDebounce) {
                this.#lastScanDateTime = currentDateTime;
                return;
            }
        }
        // Update last scan values
        this.#lastScan = decodedText;
        this.#lastScanDateTime = currentDateTime;
        this.read.emit(decodedText);
    };
    #restartScanner = () => {
        clearTimeout(this.#timeout);
        if (!this.#html5QrCode) {
            this.#startScanner();
            return;
        }
        if (this.#firstTime) {
            this.#firstTime = false;
            return;
        }
        if (this.#html5QrCode.getState() !== Html5QrcodeScannerState.SCANNING) {
            return;
        }
        // Debounce start. We should probably try to use SyncWithRAF to debounce
        // with animation frames
        this.#timeout = setTimeout(this.#startScanner, 75);
    };
    #startScanner = () => {
        const { clientWidth, clientHeight } = this.el;
        // The element is collapsed or hidden. We can't compute the aspect ratio
        if (clientHeight === 0) {
            return;
        }
        const aspectRatio = clientWidth / clientHeight;
        this.#html5QrCode = new Html5Qrcode(this.#scannerId);
        this.#html5QrCode.start(this.#currentCameraId, {
            fps: 30, // Frame per seconds for QR code scanning
            qrbox: { width: this.barcodeBoxWidth, height: this.barcodeBoxHeight },
            aspectRatio: Math.max(1, aspectRatio) // At least, aspect ratio 1/1 when the height is greater than the width
        }, this.#onScanSuccess, () => { } // Dummy error callback
        );
    };
    #destroyScanner = () => {
        if (this.#html5QrCode) {
            this.#html5QrCode.stop();
        }
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
            this.#resizeObserver = null;
        }
    };
    #setResizeObserver = () => {
        this.#resizeObserver ??= new ResizeObserver(this.#restartScanner);
        this.#resizeObserver.observe(this.el);
    };
    #selectCameraId = () => {
        if (this.cameraId) {
            this.#currentCameraId = this.cameraId;
            return;
        }
        this.#currentCameraId =
            this.cameraPreference === "Default"
                ? this.#cameras[0] // Take the first camera when default
                : cameraPreferenceDictionary[this.cameraPreference];
    };
    #initializeScanning = (scanning) => {
        if (!scanning) {
            return;
        }
        if (this.#cameras == null || this.#cameras.length === 0) {
            return;
        }
        this.#selectCameraId();
        this.#startScanner();
        this.#setResizeObserver();
    };
    componentWillLoad() {
        this.#scannerId = `ch-barcode-scanner-${autoId++}`;
    }
    componentWillUpdate() {
        if (this.#cameraIdOrScanningChange) {
            this.#cameraIdOrScanningChange = false;
            this.#initializeScanning(this.scanning);
        }
    }
    async componentDidLoad() {
        const devices = await Html5Qrcode.getCameras();
        this.#cameras = devices == null ? [] : devices.map(device => device.id);
        this.cameras.emit(this.#cameras);
        this.#initializeScanning(this.scanning);
    }
    disconnectedCallback() {
        this.#destroyScanner();
    }
    render() {
        return (h("div", { key: '53ab422755056dc21af32761b66bfa88ef0869f7', class: "ch-barcode-scanner-absolute" }, h("div", { key: '371758063345ad3532a26d833bcad2877dc7d807', id: this.#scannerId })));
    }
    static get is() { return "ch-barcode-scanner"; }
    static get originalStyleUrls() {
        return {
            "$": ["barcode-scanner.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["barcode-scanner.css"]
        };
    }
    static get properties() {
        return {
            "barcodeBoxWidth": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The width (in pixels) of the QR box displayed at the center of the video."
                },
                "attribute": "barcode-box-width",
                "reflect": false,
                "defaultValue": "200"
            },
            "barcodeBoxHeight": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The height (in pixels) of the QR box displayed at the center of the video."
                },
                "attribute": "barcode-box-height",
                "reflect": false,
                "defaultValue": "200"
            },
            "cameraId": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the ID of the selected camera. Only works if\n`cameraPreference === \"SelectedById\"`."
                },
                "attribute": "camera-id",
                "reflect": false
            },
            "cameraPreference": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"Default\" | \"FrontCamera\" | \"BackCamera\"",
                    "resolved": "\"BackCamera\" | \"Default\" | \"FrontCamera\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the camera preference for scanning."
                },
                "attribute": "camera-preference",
                "reflect": false,
                "defaultValue": "\"Default\""
            },
            "readDebounce": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how much time (in ms) should pass before to emit the read event\nwith the same last decoded text. If the last decoded text is different\nfrom the new decoded text, this property is ignored."
                },
                "attribute": "read-debounce",
                "reflect": false,
                "defaultValue": "200"
            },
            "scanning": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the control is scanning."
                },
                "attribute": "scanning",
                "reflect": false,
                "defaultValue": "true"
            }
        };
    }
    static get events() {
        return [{
                "method": "cameras",
                "name": "cameras",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the control is first rendered. Contains the ids about all\navailable cameras."
                },
                "complexType": {
                    "original": "string[]",
                    "resolved": "string[]",
                    "references": {}
                }
            }, {
                "method": "read",
                "name": "read",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when a new barcode is decoded."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "scan": {
                "complexType": {
                    "signature": "(imageFile: File) => Promise<string>",
                    "parameters": [{
                            "name": "imageFile",
                            "type": "File",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "File": {
                            "location": "global",
                            "id": "global::File"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "Scan a file a return a promise with the decoded text.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "cameraId",
                "methodName": "cameraChange"
            }, {
                "propName": "scanning",
                "methodName": "scanningChange"
            }];
    }
}
